import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Plus, ThumbsUp, MessageCircle, Calendar, User, Trash2, Image as ImageIcon, X, AtSign, Share2, Link } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface Post {
  id: string;
  titulo: string;
  conteudo: string;
  autor_nome: string;
  autor_setor: string;
  pinned: boolean;
  imagem_url?: string;
  usuarios_mencionados?: string[];
  created_at: string;
  likes_count: number;
  comments_count: number;
  user_has_liked: boolean;
}

interface Comment {
  id: string;
  texto: string;
  autor_nome: string;
  created_at: string;
  usuario_id: string;
}

interface Usuario {
  id: string;
  nome: string;
  email: string;
  setor: string;
}

export const Mural: React.FC = () => {
  const { user } = useAuth();
  const { addActivity, removeActivity } = useGamification();
  const [posts, setPosts] = useState<Post[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPost, setNewPost] = useState({
    titulo: '',
    conteudo: '',
    pinned: false,
    imagem_url: '',
    usuarios_mencionados: [] as string[]
  });
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [postComments, setPostComments] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  const canPost = true;
  const canModerate =
    user?.role === 'admin' || user?.role === 'moderador' || user?.setor === 'RH';

  const loadUsuarios = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, email, setor')
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setUsuarios(data || []);
    } catch (e) {
      console.error('[MURAL] Error loading users:', e);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get posts
      const { data: postsData, error: postsError } = await supabase
        .from('mural_posts')
        .select('*')
        .eq('publicado', true)
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      // Get likes count and user's likes for each post
      const postsWithCounts = await Promise.all((postsData || []).map(async (post) => {
        const { count: likesCount } = await supabase
          .from('mural_likes')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', post.id);

        const { data: userLike } = await supabase
          .from('mural_likes')
          .select('id')
          .eq('post_id', post.id)
          .eq('usuario_id', user.id)
          .maybeSingle();

        const { count: commentsCount } = await supabase
          .from('mural_comentarios')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', post.id);

        return {
          ...post,
          likes_count: likesCount || 0,
          comments_count: commentsCount || 0,
          user_has_liked: !!userLike,
        };
      }));

      setPosts(sortPosts(postsWithCounts));
    } catch (e: any) {
      console.error('[MURAL] Error loading posts:', e);
      toast.error('Erro ao carregar posts do mural');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const sortPosts = (arr: Post[]) =>
    [...arr].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  useEffect(() => {
    loadPosts();
    loadUsuarios();
  }, [loadPosts, loadUsuarios]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get('post');

    if (postId && posts.length > 0) {
      const sharedPost = posts.find(p => p.id === postId);
      if (sharedPost) {
        setTimeout(() => {
          loadComments(postId);
          const postElement = document.getElementById(`post-${postId}`);
          if (postElement) {
            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            postElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => {
              postElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
            }, 3000);
          }
        }, 500);
      }
    }
  }, [posts]);

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.titulo.trim() || !newPost.conteudo.trim()) {
      toast.error('Preencha todos os campos!');
      return;
    }
    try {
      const { error } = await supabase
        .from('mural_posts')
        .insert({
          usuario_id: user?.id,
          autor_nome: user?.name,
          autor_setor: user?.setor,
          titulo: newPost.titulo,
          conteudo: newPost.conteudo,
          pinned: newPost.pinned,
          publicado: true,
          imagem_url: newPost.imagem_url || null,
          usuarios_mencionados: newPost.usuarios_mencionados.length > 0 ? newPost.usuarios_mencionados : null,
        });

      if (error) throw error;

      // Add gamification points
      addActivity('post_creation', `Criou publicação: "${newPost.titulo}"`, {
        titulo: newPost.titulo,
        hasMentions: newPost.usuarios_mencionados.length > 0,
        hasImage: !!newPost.imagem_url
      });

      toast.success('Publicação criada com sucesso! +15 pontos');
      setNewPost({ titulo: '', conteudo: '', pinned: false, imagem_url: '', usuarios_mencionados: [] });
      setImagePreview('');
      setShowNewPostModal(false);
      await loadPosts();
    } catch (e: any) {
      console.error('[MURAL] Error creating post:', e);
      toast.error('Erro ao criar publicação');
    }
  };

  const handleReaction = async (postId: string, currentlyLiked: boolean) => {
    try {
      console.log('[MURAL] Processing reaction:', { postId, currentlyLiked, userId: user?.id });

      if (currentlyLiked) {
        const { error } = await supabase
          .from('mural_likes')
          .delete()
          .eq('post_id', postId)
          .eq('usuario_id', user?.id);

        if (error) {
          console.error('[MURAL] Error removing like:', error);
          throw error;
        }

        // Remove gamification points
        removeActivity('reaction', { postId });

        toast.success('Curtida removida! -2 pontos');
      } else {
        const { data, error } = await supabase
          .from('mural_likes')
          .insert({
            post_id: postId,
            usuario_id: user?.id,
          })
          .select();

        if (error) {
          console.error('[MURAL] Error adding like:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }
        console.log('[MURAL] Like added successfully:', data);

        // Add gamification points
        addActivity('reaction', 'Curtiu uma publicação', { postId });

        toast.success('Post curtido! +2 pontos');
      }

      await loadPosts();
    } catch (e) {
      console.error('[MURAL] Error processing reaction:', e);
      toast.error('Erro ao processar reação');
    }
  };

  const handleComment = async (postId: string) => {
    const commentText = (commentTexts[postId] || '').trim();
    if (!commentText) return;
    try {
      console.log('[MURAL] Adding comment:', { postId, userId: user?.id, userName: user?.name, text: commentText });

      const { data, error } = await supabase
        .from('mural_comentarios')
        .insert({
          post_id: postId,
          usuario_id: user?.id,
          autor_nome: user?.name,
          texto: commentText,
        })
        .select();

      if (error) {
        console.error('[MURAL] Error adding comment:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('[MURAL] Comment added successfully:', data);

      // Add gamification points
      addActivity('comment', 'Comentou em uma publicação', {
        postId,
        commentLength: commentText.length
      });

      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
      toast.success('Comentário adicionado! +3 pontos');
      await loadPosts();
      await loadComments(postId);
    } catch (e: any) {
      console.error('[MURAL] Error creating comment:', e);
      toast.error('Erro ao adicionar comentário');
    }
  };

  const handleSharePost = async (postId: string, postTitle: string) => {
    try {
      const shareUrl = `${import.meta.env.VITE_APP_URL || window.location.origin}/public/post?post=${postId}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiado! Pode ser compartilhado com qualquer pessoa.');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();

        try {
          document.execCommand('copy');
          toast.success('Link copiado! Pode ser compartilhado com qualquer pessoa.');
        } catch (err) {
          toast.error('Não foi possível copiar o link');
        }

        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Error sharing post:', error);
      toast.error('Erro ao compartilhar post');
    }
  };

  const loadComments = async (postId: string) => {
    if (postComments[postId]) {
      setPostComments(prev => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      return;
    }
    try {
      setLoadingComments(prev => ({ ...prev, [postId]: true }));
      const { data, error } = await supabase
        .from('mural_comentarios')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setPostComments(prev => ({ ...prev, [postId]: data || [] }));
    } catch (e) {
      console.error('[MURAL] Error loading comments:', e);
      toast.error('Erro ao carregar comentários');
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeleteComment = async (commentId: string, postId: string) => {
    if (!confirm('Tem certeza que deseja excluir este comentário?')) return;
    try {
      const { error } = await supabase
        .from('mural_comentarios')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      toast.success('Comentário removido com sucesso');
      setPostComments(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(c => c.id !== commentId),
      }));
      await loadPosts();
    } catch (e: any) {
      console.error('[MURAL] Error deleting comment:', e);
      toast.error('Erro ao excluir comentário');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta publicação? Esta ação não pode ser desfeita.')) return;

    try {
      console.log('[MURAL] Deleting post:', postId);

      // First delete all comments
      const { error: commentsError } = await supabase
        .from('mural_comentarios')
        .delete()
        .eq('post_id', postId);

      if (commentsError) {
        console.error('[MURAL] Error deleting comments:', commentsError);
      }

      // Then delete all likes
      const { error: likesError } = await supabase
        .from('mural_likes')
        .delete()
        .eq('post_id', postId);

      if (likesError) {
        console.error('[MURAL] Error deleting likes:', likesError);
      }

      // Finally delete the post
      const { error: postError } = await supabase
        .from('mural_posts')
        .delete()
        .eq('id', postId);

      if (postError) {
        console.error('[MURAL] Error deleting post:', postError);
        throw postError;
      }

      toast.success('Publicação excluída com sucesso');
      await loadPosts();
    } catch (e: any) {
      console.error('[MURAL] Error deleting post:', e);
      toast.error('Erro ao excluir publicação: ' + (e.message || 'Erro desconhecido'));
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato de imagem inválido. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    setSelectedFile(file);
    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('mural-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('mural-images')
        .getPublicUrl(fileName);

      setNewPost({ ...newPost, imagem_url: publicUrl });
      setImagePreview(publicUrl);
      toast.success('Imagem enviada com sucesso!');
    } catch (error: any) {
      console.error('[MURAL] Error uploading image:', error);
      toast.error('Erro ao enviar imagem: ' + (error.message || 'Tente novamente'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (newPost.imagem_url && selectedFile) {
      try {
        // Extract file path from URL
        const url = new URL(newPost.imagem_url);
        const path = url.pathname.split('/').slice(-2).join('/');

        await supabase.storage
          .from('mural-images')
          .remove([path]);
      } catch (error) {
        console.error('[MURAL] Error deleting image:', error);
      }
    }
    setNewPost({ ...newPost, imagem_url: '' });
    setImagePreview('');
    setSelectedFile(null);
  };

  const handleMentionUser = (userId: string) => {
    if (!newPost.usuarios_mencionados.includes(userId)) {
      setNewPost({
        ...newPost,
        usuarios_mencionados: [...newPost.usuarios_mencionados, userId],
      });
    }
    setShowMentionDropdown(false);
    setMentionSearch('');
  };

  const removeMention = (userId: string) => {
    setNewPost({
      ...newPost,
      usuarios_mencionados: newPost.usuarios_mencionados.filter(id => id !== userId),
    });
  };

  const getMentionedUsers = () => {
    return usuarios.filter(u => newPost.usuarios_mencionados.includes(u.id));
  };

  const getFilteredUsuarios = () => {
    return usuarios.filter(u =>
      u.nome.toLowerCase().includes(mentionSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(mentionSearch.toLowerCase())
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-dark underline break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-headline-lg text-on-surface dark:text-gray-100">Mural de Informações</h1>
          <button
            onClick={() => setShowNewPostModal(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark font-bold shadow-sm active:scale-95 duration-150 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Nova Publicação</span>
          </button>
        </div>

        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <MessageCircle className="w-16 h-16 text-secondary/40 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-on-surface dark:text-gray-100 mb-2">Nenhuma publicação ainda</h3>
              <p className="text-secondary dark:text-gray-400">
                Seja o primeiro a criar uma publicação!
              </p>
            </div>
          ) : (
            posts.map(post => (
              <div key={post.id} id={`post-${post.id}`} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-outline-variant dark:border-gray-700 transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-on-surface dark:text-gray-100">{post.autor_nome}</h3>
                      <div className="flex items-center space-x-2 text-sm text-secondary dark:text-gray-400">
                        <span>{post.autor_setor}</span>
                        <span>•</span>
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                        {post.pinned && (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">Fixado</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {canModerate && (
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="text-secondary/40 hover:text-error transition-colors p-2 rounded-lg hover:bg-error/10"
                      title="Excluir publicação"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-on-surface dark:text-gray-100 mb-2">{post.titulo}</h2>
                  <p className="text-on-surface dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                    {renderTextWithLinks(post.conteudo)}
                  </p>
                </div>

                {post.imagem_url && (
                  <div className="mb-4">
                    <img
                      src={post.imagem_url}
                      alt="Post"
                      className="rounded-lg max-h-96 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setExpandedImage(post.imagem_url!)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {post.usuarios_mencionados && post.usuarios_mencionados.length > 0 && (
                  <div className="mb-4 flex items-center flex-wrap gap-2">
                    <AtSign className="w-4 h-4 text-secondary" />
                    <span className="text-sm text-secondary">Mencionou:</span>
                    {post.usuarios_mencionados.map(userId => {
                      const usuario = usuarios.find(u => u.id === userId);
                      return usuario ? (
                        <span key={userId} className="text-sm bg-primary/10 text-primary px-2 py-1 rounded">
                          @{usuario.nome}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-outline-variant dark:border-gray-700">
                  <button
                    onClick={() => handleReaction(post.id, post.user_has_liked)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                      post.user_has_liked
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-primary/10 text-secondary hover:text-primary'
                    }`}
                  >
                    <ThumbsUp className={`w-4 h-4 ${post.user_has_liked ? 'fill-current' : ''}`} />
                    <span className="text-sm font-medium">{post.likes_count || 0}</span>
                  </button>

                  <div className="flex items-center space-x-2 text-secondary">
                    <MessageCircle className="w-4 h-4" />
                    <button
                      onClick={() => loadComments(post.id)}
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {post.comments_count || 0} comentários
                    </button>
                  </div>

                  <button
                    onClick={() => handleSharePost(post.id, post.titulo)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all hover:bg-primary/10 text-secondary hover:text-primary"
                    title="Compartilhar post"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Compartilhar</span>
                  </button>
                </div>

                {postComments[post.id] && (
                  <div className="mt-4 space-y-3">
                    {postComments[post.id].map(comment => (
                      <div key={comment.id} className="flex space-x-3 bg-surface-container-low dark:bg-gray-700/50 rounded-lg p-3 group">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-on-surface dark:text-gray-100">{comment.autor_nome}</span>
                            <span className="text-xs text-secondary dark:text-gray-400">{formatDate(comment.created_at)}</span>
                            {(canModerate || comment.usuario_id === user?.id) && (
                              <button
                                onClick={() => handleDeleteComment(comment.id, post.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-error/50 hover:text-error p-1"
                                title="Excluir comentário"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-on-surface dark:text-gray-200">
                            {renderTextWithLinks(comment.texto)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {loadingComments[post.id] && (
                      <div className="flex items-center justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex space-x-3 mt-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 flex space-x-2">
                    <input
                      type="text"
                      value={commentTexts[post.id] || ''}
                      onChange={(e) => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleComment(post.id)}
                      placeholder="Escreva um comentário..."
                      className="flex-1 px-3 py-2 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                    />
                    <button
                      onClick={() => handleComment(post.id)}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {showNewPostModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="bg-primary text-white p-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Nova Publicação</h2>
                <button
                  onClick={() => {
                    setShowNewPostModal(false);
                    handleRemoveImage();
                  }}
                  disabled={uploadingImage}
                  className="text-white hover:text-white/80 disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <form onSubmit={handleCreatePost} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface dark:text-gray-200 mb-2">Título</label>
                    <input
                      type="text"
                      value={newPost.titulo}
                      onChange={(e) => setNewPost({ ...newPost, titulo: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                      placeholder="Digite o título da publicação..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface dark:text-gray-200 mb-2">Conteúdo</label>
                    <textarea
                      value={newPost.conteudo}
                      onChange={(e) => setNewPost({ ...newPost, conteudo: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                      rows={6}
                      placeholder="Escreva o conteúdo da publicação..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface dark:text-gray-200 mb-2">
                      <ImageIcon className="w-4 h-4 inline mr-1" />
                      Adicionar Imagem (opcional)
                    </label>

                    {!imagePreview ? (
                      <div className="border-2 border-dashed border-outline-variant dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary transition-colors">
                        <input
                          type="file"
                          id="image-upload"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer flex flex-col items-center space-y-2"
                        >
                          <ImageIcon className="w-12 h-12 text-secondary/40" />
                          <span className="text-sm text-secondary">
                            {uploadingImage ? 'Enviando...' : 'Clique para selecionar uma imagem'}
                          </span>
                          <span className="text-xs text-secondary/40">
                            JPG, PNG, GIF ou WebP (qualquer tamanho)
                          </span>
                        </label>
                      </div>
                    ) : (
                      <div className="relative flex justify-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="rounded-lg max-w-full h-auto max-h-96"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          disabled={uploadingImage}
                          className="absolute top-2 right-2 bg-error text-white p-2 rounded-full hover:bg-error/80 shadow-lg"
                          title="Remover imagem"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      <AtSign className="w-4 h-4 inline mr-1" />
                      Mencionar Usuários
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={mentionSearch}
                        onChange={(e) => setMentionSearch(e.target.value)}
                        onFocus={() => setShowMentionDropdown(true)}
                        className="w-full px-3 py-2 border border-outline-variant dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-on-surface dark:text-gray-200"
                        placeholder="Buscar usuários..."
                      />

                      {showMentionDropdown && mentionSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-outline-variant dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {getFilteredUsuarios().map(usuario => (
                            <button
                              key={usuario.id}
                              type="button"
                              onClick={() => handleMentionUser(usuario.id)}
                              className="w-full text-left px-3 py-2 hover:bg-primary/10 transition-colors"
                            >
                              <div className="font-medium text-on-surface dark:text-gray-100">{usuario.nome}</div>
                              <div className="text-xs text-secondary dark:text-gray-400">{usuario.setor}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {getMentionedUsers().length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {getMentionedUsers().map(usuario => (
                          <span
                            key={usuario.id}
                            className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                          >
                            <span>@{usuario.nome}</span>
                            <button
                              type="button"
                              onClick={() => removeMention(usuario.id)}
                              className="hover:text-primary/80"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="pinned"
                      checked={newPost.pinned}
                      onChange={(e) => setNewPost({ ...newPost, pinned: e.target.checked })}
                      className="w-4 h-4 text-primary border-outline-variant rounded focus:ring-primary"
                    />
                    <label htmlFor="pinned" className="text-sm text-on-surface dark:text-gray-200">Fixar publicação no topo</label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewPostModal(false);
                        handleRemoveImage();
                      }}
                      disabled={uploadingImage}
                      className="flex-1 px-4 py-2 border border-outline-variant dark:border-gray-600 text-on-surface dark:text-gray-200 rounded-lg hover:bg-surface-container-low dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={uploadingImage}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark font-bold transition-colors disabled:opacity-50"
                    >
                      {uploadingImage ? 'Enviando...' : 'Publicar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {expandedImage && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-4 cursor-pointer"
            onClick={() => setExpandedImage(null)}
          >
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={expandedImage}
              alt="Imagem expandida"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};
